import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getPublicDonationReceipt,
  getPublicRange
} from "../lib/rangegridApi";
import "../modules/public-site/public-site.css";

function formatMoney(amountCents = 0, currency = "usd") {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency
  }).format(Number(amountCents || 0) / 100);
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString() : "Not recorded";
}

export function PublicDonationReceiptPage({ slug, donationId }) {
  const location = useLocation();
  const [range, setRange] = useState(null);
  const [receiptResult, setReceiptResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const sessionId = new URLSearchParams(location.search).get("session_id") || "";

  useEffect(() => {
    let cancelled = false;

    async function loadReceipt() {
      setLoading(true);
      setError("");
      try {
        const [rangeResult, receipt] = await Promise.all([
          getPublicRange(slug),
          sessionId ? getPublicDonationReceipt(slug, donationId, sessionId) : Promise.resolve(null)
        ]);
        if (!cancelled) {
          setRange(rangeResult.range);
          setReceiptResult(receipt);
          if (!sessionId) {
            setError("Checkout session is missing from this receipt link.");
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadReceipt();
    return () => {
      cancelled = true;
    };
  }, [donationId, sessionId, slug]);

  const receipt = receiptResult?.receipt;
  const donation = receiptResult?.donation;
  const rangeName = range?.name || slug;

  return (
    <main className="public-range-shell">
      <header className="public-range-hero">
        <div>
          <span className="public-range-mark">RG</span>
          <p>Donation receipt</p>
          <h1>{rangeName}</h1>
          <p>Thank you for supporting this range.</p>
        </div>
      </header>

      <section className="public-range-content">
        {loading && <div className="empty-state">Checking receipt status...</div>}
        {!loading && error && <div className="notice is-error">{error}</div>}
        {!loading && !error && receiptResult?.status === "pending" && (
          <div className="public-requirements-panel">
            <h2>Payment is still processing</h2>
            <p>Checkout returned successfully, but Stripe confirmation has not reached RangeGrid yet. Refresh this page in a moment.</p>
            <a href={`/r/${encodeURIComponent(slug)}`}>Return to range page</a>
          </div>
        )}
        {!loading && !error && receipt && (
          <div className="public-requirements-panel">
            <h2>Donation complete</h2>
            <p>Your payment has been recorded and a receipt is ready.</p>
            <dl>
              <div>
                <dt>Amount</dt>
                <dd>{formatMoney(receipt.amountCents, receipt.currency)}</dd>
              </div>
              <div>
                <dt>Receipt</dt>
                <dd>{receipt.receiptId}</dd>
              </div>
              <div>
                <dt>Donation</dt>
                <dd>{donation?.donationId || donationId}</dd>
              </div>
              <div>
                <dt>Payment</dt>
                <dd>{receipt.paymentIntentId || "Recorded"}</dd>
              </div>
              <div>
                <dt>Paid</dt>
                <dd>{formatDateTime(donation?.paidAt || receipt.createdAt)}</dd>
              </div>
              {receipt.email && (
                <div>
                  <dt>Email</dt>
                  <dd>{receipt.email}</dd>
                </div>
              )}
            </dl>
            <a href={`/r/${encodeURIComponent(slug)}`}>Return to range page</a>
          </div>
        )}
      </section>
    </main>
  );
}
